import React, {type ComponentType, ReactNode} from 'react';

import {UseQuery} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  QueryDefinition,
  skipToken,
} from '@reduxjs/toolkit/query';
import {InjectedRouter} from 'react-router';

import {GenericDocumentActionButtons} from 'app/components/general/GenericDocumentActionButtons';
import {useCurrentUserState} from 'app/current-user/api';
import {SPPage} from 'app/general/structures/SPPage';
import {getProcedureName} from 'app/utils/fallbacks';
import {isLastVersion} from 'app/version/utils';

import type {
  DocumentContentType,
  ListResponseType,
  ProcedureHashidSerializedType,
  ProcedureVersionSerializedType,
  ProcessHashidSerializedType,
  ProcessVersionSerializedType,
} from 'sweet-types';

export interface ProcedureParams {
  procedureId?: string | number;
}

export interface ProcessParams {
  processId?: string | number;
}

export type VersionParams = ProcedureParams | ProcessParams;

export type UseQueryType<FP, IT> = UseQuery<
  QueryDefinition<
    FP,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    string,
    IT,
    'sweetAPI'
  >
>;

type ItemType = ProcedureHashidSerializedType | ProcessHashidSerializedType;

type ItemVersionType =
  | ProcedureVersionSerializedType
  | ProcessVersionSerializedType;

interface Props {
  route?: {
    props: Record<string, unknown>;
  };
  router: InjectedRouter;
  params: {
    hashid: string;
    versionid: string | null;
  };
  contentType: DocumentContentType;
  children: ReactNode;
  useData: UseQueryType<string, ItemType>;
  useDataVersion: UseQueryType<string, ItemVersionType>;
  useDataVersions: UseQueryType<
    VersionParams,
    ListResponseType<ItemVersionType>
  >;
  SideBarComponent: ComponentType<
    {
      versionId: string | null;
      version: ItemVersionType | undefined;
      userId: number;
      versions: Array<ItemVersionType> | undefined;
      route?: {
        props: Record<string, unknown>;
      };
      router: InjectedRouter;
    } & (
      | {procedure: ProcedureHashidSerializedType}
      | {process: ProcessHashidSerializedType}
    )
  >;
}

export function BlankSidebarPage({
  useData,
  useDataVersion,
  useDataVersions,
  params,
  contentType,
  children,
  SideBarComponent,
  route,
  router,
}: Props) {
  const {versionid: versionId, hashid: hashId} = params;
  const {data} = useData(hashId);
  const {data: dataVersion} = useDataVersion(
    versionId == null ? skipToken : versionId
  );
  const {data: dataVersions} = useDataVersions(
    {[`${contentType}Id`]: data?.id},
    {
      skip: data == null,
    }
  );
  const user = useCurrentUserState().data!;

  const actionButtonsProps = data
    ? {
        contentType,
        document: data,
        canChange: data.can_change,
      }
    : undefined;

  const baseProps = {
    versionId,
    version: dataVersion,
    userId: user.id,
    versions: dataVersions?.results,
    route,
    router,
  };

  const sidebarProps = {
    procedure: (data: ItemType) => ({
      ...baseProps,
      procedure: data,
    }),
    proceduregroup: (data: ItemType) => ({
      ...baseProps,
      process: data,
    }),
  };

  const sidebar =
    data && sidebarProps[contentType] ? (
      <SideBarComponent {...sidebarProps[contentType](data)} />
    ) : null;

  return (
    <SPPage
      pageTitle={data?.name}
      breadcrumbProps={{
        procedureId: data?.id,
        docName: getProcedureName(data?.name),
        link: data?.html_url,
        version: dataVersion,
      }}
      horizontalPadding={false}
      actionArea={
        actionButtonsProps &&
        dataVersions &&
        (dataVersion != null ? (
          <GenericDocumentActionButtons
            version={dataVersion}
            isLastVersion={isLastVersion(dataVersion, dataVersions.results)}
            pageContext={'view-version'}
            {...actionButtonsProps}
          />
        ) : (
          <GenericDocumentActionButtons
            pageContext={'view-published'}
            {...actionButtonsProps}
          />
        ))
      }
      sidebar={sidebar}
    >
      {children}
    </SPPage>
  );
}
